/**
 * Show/hide settings for the UI: every navigation page, and the non-essential fields inside the
 * operational forms. An admin sets each key for everyone, then lists the users who get the opposite
 * (so "hidden for all except X" and "visible for all except Y" are both one row). Fields can also be
 * made mandatory. Pages are keyed by their navigation id (no registry needed); fields are registered
 * below so the settings screen can list them with a human name.
 *
 * This is a UX layer, not security: RBAC permissions still decide what a user may open or call.
 */
export type UiSettingKind = 'page' | 'field'

export interface UiSettingRow {
  key: string
  kind: UiSettingKind
  /** The default for everyone. */
  visible: boolean
  /** Fields only: overrides the form's own default when set. */
  required: boolean | null
  /** Usernames (lower-case) for whom the opposite of `visible` applies. */
  exceptions: string[]
  updatedAt?: number
  updatedBy?: string | null
}

export interface UiFieldDef {
  key: string
  form: string
  label: { en: string; ar: string }
  /** Whether the form treats it as mandatory when no setting exists. */
  defaultRequired?: boolean
}

export interface UiFormDef {
  key: string
  label: { en: string; ar: string }
  /** Navigation page id that owns the form (for grouping in the settings screen). */
  page: string
}

export const UI_FORMS: UiFormDef[] = [
  { key: 'shipments.create', label: { en: 'Create Shipment', ar: 'إنشاء شحنة' }, page: 'operations.create' },
  { key: 'txn.visit', label: { en: 'Visit Result', ar: 'نتيجة الزيارة' }, page: 'operations.visit' },
  { key: 'txn.rts', label: { en: 'Return to Shipper', ar: 'مرتجع للراسل' }, page: 'operations.rts' },
]

const field = (form: string, name: string, en: string, ar: string, defaultRequired = false): UiFieldDef => ({ key: `${form}.${name}`, form, label: { en, ar }, defaultRequired })

/** Only fields a dispatcher could reasonably skip are listed — identity fields (client, phone, address, amount) are never toggleable. */
export const UI_FIELDS: UiFieldDef[] = [
  field('shipments.create', 'clientSearch', 'Find client (search box)', 'بحث عن العميل'),
  field('shipments.create', 'product', 'Product', 'المنتج'),
  field('shipments.create', 'mobile', 'Other mobile', 'موبايل آخر'),
  field('shipments.create', 'cod', 'COD amount', 'مبلغ التحصيل'),
  field('shipments.create', 'weight', 'Weight', 'الوزن', true),
  field('shipments.create', 'pieces', 'Pieces', 'عدد القطع'),
  field('shipments.create', 'shipmentType', 'Shipment type', 'نوع الشحنة'),
  field('shipments.create', 'ref', 'Reference', 'المرجع'),
  field('shipments.create', 'contents', 'Contents', 'المحتوى'),
  field('shipments.create', 'declaredValue', 'Declared value', 'القيمة المعلنة'),
  field('shipments.create', 'allowToOpen', 'Allow opening', 'مسموح بالفتح'),
  field('shipments.create', 'specialInstructions', 'Special instructions', 'تعليمات خاصة'),
  field('txn.visit', 'reasonId', 'Reason', 'السبب'),
  field('txn.visit', 'recipientName', 'Recipient name', 'اسم المستلم'),
  field('txn.visit', 'postponedTo', 'Postponed to', 'مؤجل إلى'),
  field('txn.visit', 'notes', 'Notes', 'ملاحظات'),
  field('txn.rts', 'notes', 'Notes', 'ملاحظات'),
]

export const UI_FIELD_BY_KEY: Record<string, UiFieldDef> = Object.fromEntries(UI_FIELDS.map((f) => [f.key, f]))

export interface EffectiveUiSetting {
  visible: boolean
  required: boolean
}

/** What one user actually gets for a key. Missing row = the code's own default. */
export function effectiveUiSetting(row: UiSettingRow | undefined, username: string | null | undefined, defaultRequired = false): EffectiveUiSetting {
  if (!row) return { visible: true, required: defaultRequired }
  const u = (username ?? '').toLowerCase()
  const flipped = Boolean(u) && row.exceptions.includes(u)
  return { visible: flipped ? !row.visible : row.visible, required: row.required ?? defaultRequired }
}
